import React, { useState, useEffect, useRef } from 'react';
import './MessengerPanel.css';
import ContactsSidebar from './components/ContactsSidebar';
import ChatHeader from './components/ChatHeader';
import MessagesContainer from './components/MessagesContainer';
import MessageInputArea from './components/MessageInputArea';
import ContactInfoView from './components/ContactInfoView';
import Modal from 'react-modal';
import { useAuth } from '../../contexts/authContext';
import supabase from "../../supabaseClient";
import { v4 as uuidv4 } from 'uuid';
import { useMessagesUnread } from '../../contexts/MessagesUnreadContext.jsx';
import { useChatDock } from '../../contexts/ChatDockContext';
import { useCall } from '../../contexts/CallContext';
import getMessagingErrorMessage from '../../utils/messagingErrors';

const MessengerPanel = ({ isOpen, onClose, mode = 'panel', initialContactId = null }) => {
  const { user } = useAuth();
  const unreadCtx = useMessagesUnread();
  const chatDock = useChatDock();
  const call = useCall();

  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [mobileActiveView, setMobileActiveView] = useState('list');

  // Strict read detection state
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const readTriggeredRef = useRef(false);
  const [replyingTo, setReplyingTo] = useState(null);

  const dockContacts = chatDock?.sortedContacts || chatDock?.contacts || [];
  const dockRequestContacts = chatDock?.sortedRequestContacts || chatDock?.requestContacts || [];
  const [anyoneDirects, setAnyoneDirects] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [allowMessagesFromAnyone, setAllowMessagesFromAnyone] = useState(false);
  const [showMessengerSettings, setShowMessengerSettings] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [lastMessages, setLastMessages] = useState({});
  const [lastSenderNames, setLastSenderNames] = useState({});
  const [senderProfiles, setSenderProfiles] = useState({});
  const unreadCounts = unreadCtx?.unreadCounts || {};
  const [groupMembers, setGroupMembers] = useState([]);
  const [enrichedContacts, setEnrichedContacts] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [contactTyping, setContactTyping] = useState(false);
  const [contactOnline, setContactOnline] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(false);
  const typingTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const panelRef = useRef(null);
  const [settingsPos, setSettingsPos] = useState({ top: 0, left: 16, width: 360 });

  // View mode: 'chat' or 'contactInfo'
  const [viewMode, setViewMode] = useState('chat');

  useEffect(() => {
    const update = () => {
      setIsMobileLayout(window.innerWidth <= 768);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!isMobileLayout) return;
    setMobileActiveView(activeContact ? 'chat' : 'list');
  }, [activeContact, isMobileLayout]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const shouldLock = mode === 'panel' && isOpen && isMobileLayout;
    if (!shouldLock) {
      document.body.classList.remove('messenger-panel-scroll-lock');
      return;
    }
    document.body.classList.add('messenger-panel-scroll-lock');
    return () => {
      document.body.classList.remove('messenger-panel-scroll-lock');
    };
  }, [isOpen, isMobileLayout, mode]);

  // Recording state for panel composer
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoChunksRef = useRef([]);
  const [pendingAudioFile, setPendingAudioFile] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);

  // Fetch allow_messages_from_anyone setting
  useEffect(() => {
    if (!user) return;
    const fetchAllowMessagesSetting = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('allow_messages_from_anyone')
        .eq('id', user.id)
        .single();
      if (!error && data) {
        setAllowMessagesFromAnyone(data.allow_messages_from_anyone === true);
      }
    };
    fetchAllowMessagesSetting();
  }, [user]);

  // Set activeContact from initialContactId prop (for deep links)
  useEffect(() => {
    if (initialContactId && !activeContact) {
      setActiveContact(initialContactId);
      if (chatDock?.openChat) {
        // Load messages for this contact, but do not spawn a ChatDock window
        // when the Messenger panel is driving the conversation.
        chatDock.openChat(initialContactId, { spawnWindow: false });
      }
    }
  }, [initialContactId, activeContact, chatDock]);

  // Helper: upload a media file (audio/video) and create a message
  const uploadMediaFile = async (file, label) => {
    if (!file || !user || !activeContact) return;
    setIsUploading(true);
    setUploadProgress(0);
    let optimisticMsg = null;
    try {
      const activeContactObj = contacts.find(c => c.id === activeContact);
      const isGroupType = activeContactObj?.type === 'group' || activeContactObj?.type === 'academy_group';
      const fileExt = (file.name.split('.').pop()) || 'webm';
      const fileName = `${uuidv4()}.${fileExt}`;
      // Save composer audio/video to messages/ subfolder (not shown in Media & Files)
      const filePath = `messages/${user.id}/${fileName}`;
      const { error } = await supabase.storage.from('chat-files').upload(filePath, file);
      if (error) throw error;
      const { data: publicUrlData } = supabase.storage.from('chat-files').getPublicUrl(filePath);
      const fileUrl = publicUrlData.publicUrl;
      const created_at = new Date().toISOString();
      const msgPayload = {
        sender_id: user.id,
        receiver_id: !isGroupType ? activeContact : null,
        conversation_id: isGroupType ? activeContact : null,
        media_url: fileUrl,
        content: label || file.name,
        created_at
      };
      // Optimistic UI: append message immediately so it shows without waiting for realtime
      if (chatDock?.appendMessage) {
        chatDock.appendMessage(activeContact, msgPayload);
      }
      optimisticMsg = msgPayload;
      const { error: insertError } = await supabase.from('messages').insert([msgPayload]);
      if (insertError) {
        chatDock?.removeOptimisticMessage?.(activeContact, optimisticMsg);
        alert(getMessagingErrorMessage(insertError));
        return;
      }
    } catch (err) {
      if (optimisticMsg) {
        chatDock?.removeOptimisticMessage?.(activeContact, optimisticMsg);
      }
      alert('Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }
  };

  // Audio recording handlers
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const mediaRecorder = new window.MediaRecorder(stream);
      audioRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
        setPendingAudioFile(file);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setIsRecordingAudio(true);
    } catch (e) {
      alert('Audio recording not available');
    }
  };

  const handleStopRecording = () => {
    audioRecorderRef.current?.stop();
    setIsRecordingAudio(false);
  };

  const handleSendAudio = async () => {
    if (pendingAudioFile) {
      await uploadMediaFile(pendingAudioFile, 'Audio message');
      setPendingAudioFile(null);
    }
  };

  const handleClearAudio = () => {
    if (isRecordingAudio) handleStopRecording();
    setPendingAudioFile(null);
  };

  // Video recording handlers
  const handleStartVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      recordingStreamRef.current = stream;
      const mediaRecorder = new window.MediaRecorder(stream);
      videoRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => videoChunksRef.current.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const file = new File([blob], `video_${Date.now()}.webm`, { type: 'video/webm' });
        setPendingVideoFile(file);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setIsRecordingVideo(true);
    } catch (e) {
      alert('Video recording not available');
    }
  };

  const handleStopVideoRecording = () => {
    videoRecorderRef.current?.stop();
    setIsRecordingVideo(false);
  };

  const handleSendVideo = async () => {
    if (pendingVideoFile) {
      await uploadMediaFile(pendingVideoFile, 'Video message');
      setPendingVideoFile(null);
    }
  };

  const handleClearVideo = () => {
    if (isRecordingVideo) handleStopVideoRecording();
    setPendingVideoFile(null);
  };

  const handleToggleAllowMessages = async () => {
    const newValue = !allowMessagesFromAnyone;
    setAllowMessagesFromAnyone(newValue);
    if (user) {
      await supabase
        .from('profiles')
        .update({ allow_messages_from_anyone: newValue })
        .eq('id', user.id);
    }
  };

  // Derived contacts list: base from ChatDockContext, optionally extended when "anyone" mode is on
  const baseContacts = dockContacts || [];
  const contacts = allowMessagesFromAnyone
    ? (() => {
        const merged = [...baseContacts, ...anyoneDirects];
        return merged.filter((c, index, self) =>
          index === self.findIndex(other => other.id === c.id && other.type === c.type)
        );
      })()
    : baseContacts;

  const chatMessages = (chatDock?.messagesById && activeContact)
    ? (chatDock.messagesById[activeContact] || [])
    : [];

  // Fetch extra direct contacts when "allow messages from anyone" is enabled
  useEffect(() => {
    if (!user) { setAnyoneDirects([]); return; }
    if (!allowMessagesFromAnyone) { setAnyoneDirects([]); return; }

    const run = async () => {
      const { data: allProfiles } = await supabase
        .from('profiles')
        .select('id, name, email, avatar_url')
        .neq('id', user.id);

      const baseDirectIds = new Set(
        (baseContacts || [])
          .filter(c => c.type === 'direct')
          .map(c => c.id)
      );

      const extras = (allProfiles || [])
        .filter(p => !baseDirectIds.has(p.id))
        .map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          avatar_url: u.avatar_url,
          type: 'direct'
        }));

      setAnyoneDirects(extras);
    };

    run();
  }, [user, allowMessagesFromAnyone, baseContacts]);

  // Enrich contacts (profiles + groups) for hover cards
  useEffect(() => {
    if (!user || contacts.length === 0) return;

    const run = async () => {
      const directIds = contacts.filter(c => c.type === 'direct').map(c => c.id);
      if (directIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url, bio, location, native_languages, target_languages')
          .in('id', directIds);
        const enriched = {};
        (profiles || []).forEach(p => {
          enriched[p.id] = { ...p, display_name: p.name || p.email };
        });
        setEnrichedContacts(prev => ({ ...prev, ...enriched }));
      }

      const groupIds = contacts.filter(c => c.type === 'group').map(c => c.id);
      if (groupIds.length > 0) {
        const { data: groups } = await supabase
          .from('groups')
          .select('id, name, description, group_banner_url, language, level')
          .in('id', groupIds);
        const enrichedGroups = {};
        (groups || []).forEach(g => {
          enrichedGroups[g.id] = { ...g, display_name: g.name };
        });
        setEnrichedContacts(prev => ({ ...prev, ...enrichedGroups }));
      }
    };

    run();
  }, [user, contacts]);

  // Fetch last messages (unread Counts come from MessagesUnreadContext)
  useEffect(() => {
    if (!user || contacts.length === 0) return;
    let cancelled = false;
    const fetchLasts = async () => {
      const entries = await Promise.all(contacts.map(async (c) => {
        let data, error;
        if (c.type === 'direct') {
          const res = await supabase
            .from('messages')
            .select('id, content, media_url, created_at, sender_id, receiver_id, conversation_id')
            .or(`and(sender_id.eq.${user.id},receiver_id.eq.${c.id}),and(sender_id.eq.${c.id},receiver_id.eq.${user.id})`)
            .order('created_at', { ascending: false })
            .limit(1);
          data = res.data; error = res.error;
        } else {
          const res = await supabase
            .from('messages')
            .select('id, content, media_url, created_at, sender_id, receiver_id, conversation_id')
            .eq('conversation_id', c.id)
            .order('created_at', { ascending: false })
            .limit(1);
          data = res.data; error = res.error;
        }
        const last = data && data.length > 0 ? data[0] : null;
        return [c.id, last];
      }));
      if (!cancelled) {
        const map = Object.fromEntries(entries);
        setLastMessages(map);
      }
    };
    fetchLasts();
    return () => { cancelled = true; };
  }, [contacts, user]);

  // Send message handler
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !user || !activeContact) return;
    if (chatDock && chatDock.sendMessage) {
      const res = await chatDock.sendMessage(activeContact, messageInput, replyingTo?.id || null);
      if (!res?.success) {
        alert(getMessagingErrorMessage(res?.error));
        return;
      }
    }
    setMessageInput('');
    setIsTyping(false);
    setReplyingTo(null);
  };

  const handleFileUpload = async (file) => {
    if (!user || !activeContact) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const activeContactObj = contacts.find(c => c.id === activeContact);
      const isGroupType = activeContactObj?.type === 'group' || activeContactObj?.type === 'academy_group';
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      // Save picker files to attachments/ subfolder (shown in Media & Files)
      const filePath = `attachments/${user.id}/${fileName}`;
      const { error: uploadError } = await supabase.storage
        .from('chat-files')
        .upload(filePath, file);
      if (uploadError) {
        alert('File upload failed.');
        setIsUploading(false);
        return;
      }
      const { data: publicUrlData } = supabase.storage.from('chat-files').getPublicUrl(filePath);
      const fileUrl = publicUrlData.publicUrl;
      const { error: insertError } = await supabase
        .from('messages')
        .insert([{
          sender_id: user.id,
          receiver_id: !isGroupType ? activeContact : null,
          conversation_id: isGroupType ? activeContact : null,
          media_url: fileUrl,
          content: file.name,
          created_at: new Date().toISOString()
        }]);
      if (insertError) {
        alert(getMessagingErrorMessage(insertError));
      }
    } catch (err) {
      alert('File upload failed.');
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setMessageInput(prev => prev + emoji);
  };

  const handleGifSelect = async (gifUrl) => {
    if (!user || !activeContact) return;
    const activeContactObj = contacts.find(c => c.id === activeContact);
    const isGroupType = activeContactObj?.type === 'group' || activeContactObj?.type === 'academy_group';
    try {
      const { error } = await supabase
        .from('messages')
        .insert([{
          sender_id: user.id,
          receiver_id: !isGroupType ? activeContact : null,
          conversation_id: isGroupType ? activeContact : null,
          media_url: gifUrl,
          content: 'GIF',
          created_at: new Date().toISOString()
        }]);

      if (error) {
        alert(getMessagingErrorMessage(error));
      }
    } catch (e) {
      alert(getMessagingErrorMessage(e));
    }
  };

  const handleSelectContact = (contactId) => {
    setActiveContact(contactId);
    if (isMobileLayout) {
      setMobileActiveView('chat');
    }
    if (chatDock && chatDock.openChat) {
      // Load messages without opening a separate ChatDock window so the
      // Messenger panel remains the primary conversation surface.
      chatDock.openChat(contactId, { spawnWindow: false });
    }
  };

  const handleMobileBackToList = () => {
    setMobileActiveView('list');
  };

  const filteredContacts = contacts.filter((c) => {
    const label = (c.name || c.email || '').toLowerCase();
    return label.includes(contactQuery.toLowerCase());
  });

  const handleMarkThreadUnread = async (contact) => {
    if (!unreadCtx?.markThreadUnread || !contact) return;
    await unreadCtx.markThreadUnread(contact);
  };

  // Mark thread read when opened in panel, using shared context
  useEffect(() => {
    const run = async () => {
      if (!user || !activeContact || !unreadCtx?.markThreadRead) return;
      const entry = contacts.find(c => c.id === activeContact);
      if (!entry) return;
      await unreadCtx.markThreadRead(activeContact, entry.type || 'direct');
    };
    run();
  }, [user, activeContact, contacts, unreadCtx]);

  // Reset read trigger when active contact changes
  useEffect(() => {
    readTriggeredRef.current = false;
  }, [activeContact]);

  // Reset viewMode to 'chat' when activeContact changes
  useEffect(() => {
    setViewMode('chat');
  }, [activeContact]);

  // Strict read detection: only mark read when input focused AND scrolled near bottom
  useEffect(() => {
    if (!activeContact || !user?.id) return;
    const entry = contacts.find(c => c.id === activeContact);
    if (!entry || entry.type !== 'direct') return;
    if (!isInputFocused || !isNearBottom) return;
    if (readTriggeredRef.current) return;

    const hasUnreadIncoming = chatMessages.some(m =>
      m?.sender_id === activeContact &&
      m?.receiver_id === user.id &&
      !m?.read_at
    );

    if (!hasUnreadIncoming) return;
    readTriggeredRef.current = true;
    chatDock?.markDirectThreadReadIfNeeded?.(activeContact);
  }, [activeContact, user?.id, isInputFocused, isNearBottom, chatMessages, contacts, chatDock]);

  const loadBlockedUsers = async () => {
    if (!user?.id) {
      setBlockedUsers([]);
      return;
    }
    setBlockedUsersLoading(true);
    try {
      const { data: blocks, error } = await supabase
        .from('blocked_users')
        .select('blocked_user_id, profiles!blocked_users_blocked_user_id_fkey(id, name, email, avatar_url)')
        .eq('user_id', user.id);
      
      if (!error && blocks) {
        const enriched = blocks.map(b => ({
          id: b.blocked_user_id,
          name: b.profiles?.name || b.profiles?.email || 'User',
          email: b.profiles?.email,
          avatar_url: b.profiles?.avatar_url
        }));
        setBlockedUsers(enriched);
      }
    } catch (err) {
      console.error('Failed to load blocked users:', err);
    } finally {
      setBlockedUsersLoading(false);
    }
  };

  const handleUnblock = async (blockedUserId) => {
    if (!user?.id || !blockedUserId) return;
    try {
      const { error } = await supabase
        .from('blocked_users')
        .delete()
        .eq('user_id', user.id)
        .eq('blocked_user_id', blockedUserId);
      
      if (!error) {
        setBlockedUsers(prev => prev.filter(u => u.id !== blockedUserId));
      }
    } catch (err) {
      console.error('Failed to unblock user:', err);
    }
  };

  const openSettings = () => {
    loadBlockedUsers();
    // Measure panel position now
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      const gap = 12;
      const available = Math.max(120, rect.left - gap - 16);
      const width = Math.min(360, Math.max(240, available));
      const left = Math.max(8, rect.left - width - gap);
      const top = Math.max(16, rect.top + gap);
      console.log('[MessengerSettings] Panel rect:', rect);
      console.log('[MessengerSettings] Computed position:', { top, left, width });
      // Set position first
      setSettingsPos({ top, left, width });
      // Open modal in next tick after state update
      setTimeout(() => setShowMessengerSettings(true), 0);
    } else {
      setShowMessengerSettings(true);
    }
  };

  // In page mode, always render; in panel mode, only render when open
  if (mode === 'panel' && !isOpen) return null;

  const isPageMode = mode === 'page';

  return (
    <>
      {/* Overlay - only for panel mode */}
      {!isPageMode && <div className="messenger-panel-overlay" onClick={onClose}></div>}
      
      {/* Panel or Page */}
      <div className={`messenger-panel ${isPageMode ? 'page-mode' : ''} ${isOpen ? 'open' : ''}`} ref={panelRef}>
        {/* Header - only for panel overlay (no page mode link) */}
        {!isPageMode && (
          <div className="messenger-panel-header">
            <h2>Messenger</h2>
            <button className="messenger-panel-close" onClick={onClose} title="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}
        
        <div className="messenger-panel-content">
          <div
            className={`messenger-container ${isMobileLayout && mobileActiveView === 'chat' ? 'mobile-chat-open' : ''}`}
          >
            <ContactsSidebar
              contacts={filteredContacts}
              requestContacts={dockRequestContacts}
              activeContact={activeContact}
              setActiveContact={handleSelectContact}
              onEditGroup={() => {}}
              onRemoveGroup={() => {}}
              onCreateGroup={() => {}}
              onOpenSettings={openSettings}
              onSearchChange={setContactQuery}
              lastMessages={lastMessages}
              lastSenderNames={lastSenderNames}
              unreadCounts={unreadCounts}
              onMarkUnread={handleMarkThreadUnread}
              userId={user?.id}
              enrichedContacts={enrichedContacts}
              allowMessagesFromAnyone={allowMessagesFromAnyone}
              onToggleAllowMessages={handleToggleAllowMessages}
            />
            
            {activeContact ? (
              viewMode === 'contactInfo' ? (
                <div className="messenger-chat-area">
                  <ContactInfoView
                    contact={contacts.find(c => c.id === activeContact)}
                    enrichedData={enrichedContacts[activeContact]}
                    onBack={() => setViewMode('chat')}
                    conversationId={activeContact}
                    userId={user?.id}
                  />
                </div>
              ) : (
              <div className="messenger-chat-area">
                <ChatHeader
                  contact={contacts.find(c => c.id === activeContact)}
                  onBack={isMobileLayout && mobileActiveView === 'chat' ? handleMobileBackToList : undefined}
                  contactOnline={contactOnline}
                  groupMembers={groupMembers}
                  activeCall={call?.activeCallsByThread?.[activeContact]}
                  onOpenContactInfo={() => setViewMode('contactInfo')}
                  onJoinCall={() => {
                    const activeCallInfo = call?.activeCallsByThread?.[activeContact];
                    const contact = contacts.find(c => c.id === activeContact);
                    if (activeCallInfo && contact && call?.joinCall) {
                      call.joinCall({
                        callId: activeCallInfo.callId,
                        roomId: activeCallInfo.roomId,
                        mode: activeCallInfo.mode,
                        thread: {
                          id: contact.id,
                          type: contact.type || 'group',
                          name: contact.name || contact.email,
                        },
                      });
                    }
                  }}
                  onStartVideoCall={() => {
                    const contact = contacts.find(c => c.id === activeContact);
                    if (contact && call?.startCall) {
                      call.startCall({
                        thread: {
                          id: contact.id,
                          type: contact.type || 'direct',
                          name: contact.name || contact.email,
                          avatar: contact.avatar_url || contact.group_banner_url,
                          memberIds: groupMembers?.map(m => m.id) || [],
                        },
                        mode: 'video',
                      });
                    }
                  }}
                  onStartAudioCall={() => {
                    const contact = contacts.find(c => c.id === activeContact);
                    if (contact && call?.startCall) {
                      call.startCall({
                        thread: {
                          id: contact.id,
                          type: contact.type || 'direct',
                          name: contact.name || contact.email,
                          avatar: contact.avatar_url || contact.group_banner_url,
                          memberIds: groupMembers?.map(m => m.id) || [],
                        },
                        mode: 'audio',
                      });
                    }
                  }}
                  onInfo={() => {}}
                />
                <MessagesContainer
                  chatMessages={chatMessages}
                  reactions={[]}
                  onAddReaction={(messageId, emoji) => chatDock?.addReaction?.(messageId, emoji)}
                  onDeleteMessage={(messageId) => chatDock?.deleteMessage?.(activeContact, messageId)}
                  onEditMessage={(messageId, newContent) => chatDock?.editMessage?.(activeContact, messageId, newContent)}
                  userId={user?.id}
                  isGroupThread={(() => {
                    const t = contacts.find(c => c.id === activeContact)?.type;
                    return t === 'group' || t === 'academy_group';
                  })()}
                  senderProfiles={senderProfiles}
                  onScroll={(e) => {
                    const el = e.target;
                    const threshold = 80;
                    const near = (el.scrollHeight - (el.scrollTop + el.clientHeight)) < threshold;
                    setIsNearBottom(near);
                  }}
                  onReply={(msg) => setReplyingTo(msg)}
                />
                {/* Reply preview bar */}
                {replyingTo && (
                  <div className="message-reply-preview">
                    <div className="message-reply-info">
                      <span className="message-reply-label">Replying to</span>
                      <span className="message-reply-text">{replyingTo.content?.slice(0, 50)}{replyingTo.content?.length > 50 ? '…' : ''}</span>
                    </div>
                    <button className="message-reply-cancel" onClick={() => setReplyingTo(null)} title="Cancel reply">×</button>
                  </div>
                )}
                <MessageInputArea
                  messageInput={messageInput}
                  setMessageInput={setMessageInput}
                  handleSendMessage={handleSendMessage}
                  isRecording={isRecordingAudio}
                  isVideoRecording={isRecordingVideo}
                  handleStartRecording={handleStartRecording}
                  handleStopRecording={handleStopRecording}
                  handleStartVideoRecording={handleStartVideoRecording}
                  handleStopVideoRecording={handleStopVideoRecording}
                  handleFileUpload={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (file) {
                      handleFileUpload(file);
                    }
                  }}
                  handleStartCall={() => {}}
                  handleTyping={(e) => setMessageInput(e.target.value)}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  onEmojiSelect={handleEmojiSelect}
                  onGifSelect={handleGifSelect}
                  onSendAudio={handleSendAudio}
                  onSendVideo={handleSendVideo}
                  onClearAudio={handleClearAudio}
                  onClearVideo={handleClearVideo}
                  pendingAudioFile={pendingAudioFile}
                  pendingVideoFile={pendingVideoFile}
                  videoStream={isRecordingVideo ? recordingStreamRef.current : null}
                  onInputFocus={() => setIsInputFocused(true)}
                  onInputBlur={() => setIsInputFocused(false)}
                />
              </div>
            )) : (
              <div className="messenger-empty-state">
                <p>Select a conversation to start messaging</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer band - only for panel mode */}
        {!isPageMode && <div className="messenger-panel-footer" aria-hidden="true" />}

        {/* Settings Modal */}
        <Modal
          isOpen={showMessengerSettings}
          onRequestClose={() => setShowMessengerSettings(false)}
          contentLabel="Messenger Settings"
          ariaHideApp={false}
          style={{
            overlay: {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(2px)',
              zIndex: 100000,
            },
            content: {
              position: 'fixed',
              top: `${settingsPos.top}px`,
              left: `${settingsPos.left}px`,
              right: 'auto',
              bottom: 'auto',
              width: `${settingsPos.width}px`,
              maxWidth: 'calc(100% - 32px)',
              border: '2px solid #38bdf8',
              borderRadius: '12px',
              background: '#1e293b',
              color: '#e2e8f0',
              boxShadow: '0 10px 30px rgba(0,0,0,0.35), 0 0 12px rgba(56,189,248,0.35)',
              padding: '16px',
              margin: 0,
              transform: 'none' // disable react-modal default centering
            }
          }}
        >
          <h2>Messenger Settings</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
            <span>Allow messages from anyone</span>
            <button
              type="button"
              onClick={handleToggleAllowMessages}
              aria-pressed={allowMessagesFromAnyone}
              style={{
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                background: 'transparent',
              }}
            >
              <span
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 999,
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center',
                  background: allowMessagesFromAnyone ? 'var(--primary-light, #38bdf8)' : 'rgba(148,163,184,0.6)',
                  transition: 'background 0.18s ease',
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    background: '#ffffff',
                    transform: allowMessagesFromAnyone ? 'translateX(18px)' : 'translateX(0)',
                    transition: 'transform 0.18s ease',
                  }}
                />
              </span>
            </button>
          </div>

          {/* Blocked Users Section */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(148,163,184,0.2)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>Blocked Users</h3>
            {blockedUsersLoading ? (
              <div style={{ padding: 12, color: 'rgba(226,232,240,0.6)', fontSize: '0.9rem' }}>Loading...</div>
            ) : blockedUsers.length === 0 ? (
              <div style={{ padding: 12, color: 'rgba(226,232,240,0.6)', fontSize: '0.9rem' }}>No blocked users</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {blockedUsers.map(blocked => (
                  <div key={blocked.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: 8 }}>
                    {blocked.avatar_url ? (
                      <img src={blocked.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(148,163,184,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 600 }}>
                        {(blocked.name || 'U')[0].toUpperCase()}
                      </div>
                    )}
                    <div style={{ flex: 1, fontSize: '0.9rem' }}>
                      {blocked.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUnblock(blocked.id)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid rgba(34,197,94,0.4)',
                        background: 'rgba(34,197,94,0.15)',
                        color: '#22c55e',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => { e.target.style.background = 'rgba(34,197,94,0.25)'; }}
                      onMouseLeave={(e) => { e.target.style.background = 'rgba(34,197,94,0.15)'; }}
                    >
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setShowMessengerSettings(false)}>Close</button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default MessengerPanel;
